import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, withCtx as _withCtx, createBlock as _createBlock, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "mt-4" }
const _hoisted_3 = { class: "mt-4" }
const _hoisted_4 = { class: "mt-4" }
const _hoisted_5 = { class: "mt-4" }
const _hoisted_6 = { class: "mt-4" }
const _hoisted_7 = { class: "mt-4" }
const _hoisted_8 = { class: "mt-4" }
const _hoisted_9 = { class: "mt-4" }
const _hoisted_10 = { class: "mt-4" }

import CategoriesField from '@/components/categories/CategoriesField.vue';
import CategoryField from '@/components/categories/CategoryField.vue';
import AddressField from '@/components/common/AddressField.vue';
import ContactField from '@/components/common/ContactField.vue';
import RichTextField from '@/components/common/RichTextField.vue';
import MediaList from '@/components/media/MediaList.vue';
import InitiativeState from '@/components/initiative/InitiativeState.vue';
import { Contact } from '@/models/contact.model';
import {
  impactAreaItems,
  Initiative,
  defaultInitiative,
  InitiativeStatus,
} from '@/models/initiative.model';
import { MediaPurpose } from '@/models/media.model';
import { useEvents, Event } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { ContentStoreActions, ContentStoreState } from '@/stores/content.store';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { User, UserLevel } from '@/models/user.model';
import HelpDialog from '@/components/common/HelpDialog.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'InitiativeEditView',
  emits: [
  'update:modelValue',
  'update:published',
  'remove-contact',
],
  setup(__props, { emit: __emit }) {

const route = useRoute();
const store = useStore<ShiftCMSState & { cns: ContentStoreState }>();
const events = useEvents();
const uuid = route.params.uuid as string;
const { t } = useI18n();

const user = computed<User | null>(() => store.state.loggedInUser);

const value = ref<Initiative>(defaultInitiative);
let currentValue: Initiative = { ...defaultInitiative };
const valid = ref<boolean>(true);

const emit = __emit;

let translatedAreaItems = impactAreaItems.map((i) => ({
  title: t(`initiative.edit.area_${i.value}`),
  value: i.value,
}));

const update = () => {
  emit('update:modelValue', value.value);
};

const removeContact = (item: Contact) => {
  emit('remove-contact', item);
};

const updateLanguage = async () => {
  const initiative: Initiative = await store.dispatch(
    ContentStoreActions.GetInitiativeByUUID,
    uuid,
  );
  currentValue = { ...initiative }; // store the current value
  value.value = { ...initiative };
  translatedAreaItems = impactAreaItems.map((i) => ({
    title: t(`initiative.edit.area_${i.value}`),
    value: i.value,
  }));
};

const save = async () => {
  if (!valid.value) {
    return;
  }

  const initiative: Initiative = await store.dispatch(
    ContentStoreActions.UpdateInitiativeByUUID,
    { uuid, initiative: value.value },
  );
  if (initiative?.id) {
    currentValue = { ...initiative }; // store the current value
    value.value = { ...initiative };
  }
};

const setReady = async () => {
  const initiative: Initiative = await store.dispatch(
    ContentStoreActions.UpdateInitiativeStatusByUUID,
    {
      uuid,
      initiative: { ...value.value, status: InitiativeStatus.ReadyForReview },
    },
  );
  if (initiative?.id) {
    value.value = { ...initiative };
  }
};

const setReadyForPublishing = async () => {
  const initiative: Initiative = await store.dispatch(
    ContentStoreActions.UpdateInitiativeStatusByUUID,
    {
      uuid,
      initiative: {
        ...value.value,
        status: InitiativeStatus.ReadyForPublishing,
      },
    },
  );
  if (initiative?.id) {
    value.value = { ...initiative };
  }
};

const setBackToEditing = async () => {
  const initiative: Initiative = await store.dispatch(
    ContentStoreActions.UpdateInitiativeStatusByUUID,
    {
      uuid,
      initiative: {
        ...value.value,
        status: InitiativeStatus.Editing,
      },
    },
  );
  if (initiative?.id) {
    value.value = { ...initiative };
  }
};

function share() {
  navigator.clipboard.writeText(
    `${document.location.protocol}//${document.location.host}/#/initiative/${value.value.publicUUID}/edit`,
  );
  store.dispatch(Actions.ShowNotification, {
    message: 'Copied to clipboard',
    type: 'success',
  });
}

onMounted(() => {
  events.on(Event.LanguageChanged, updateLanguage);
  updateLanguage();
});

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateLanguage);
});

const disabled = computed(
  () =>
    (value.value.status !== InitiativeStatus.Editing && !user.value) ||
    (![InitiativeStatus.Editing, InitiativeStatus.ReadyForReview].includes(
      value.value.status,
    ) &&
      user.value?.level === UserLevel.ContentEditor),
);

const rules = {
  required: (value: string) => !!value || t('initiative.edit.required'),
};

function getSimpleValue(i: Initiative) {
  return {
    title: i.title,
    quizTitle: i.quizTitle,
    description: i.description,
    shortDescription: i.shortDescription,
    website: i.website,
    area: i.impactArea,
    categories: i.categories.map((c) => c.id),
    address: i.address,
    media: i.media.map((c) => c.id),
  };
}

const isDirty = computed<boolean>(() => {
  const isDirty =
    JSON.stringify(getSimpleValue(value.value)) !==
    JSON.stringify(getSimpleValue(currentValue));

  store.commit(Mutations.SetDirtyState, isDirty);

  return isDirty;
});

return (_ctx: any,_cache: any) => {
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_timeline_item = _resolveComponent("v-timeline-item")!
  const _component_v_timeline = _resolveComponent("v-timeline")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createBlock(_component_v_container, null, {
    default: _withCtx(() => [
      _createVNode(_component_v_form, {
        modelValue: valid.value,
        "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((valid).value = $event)),
        onSubmit: _withModifiers(save, ["prevent"])
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_row, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, { cols: "9" }, {
                default: _withCtx(() => [
                  _createElementVNode("h1", null, [
                    _createTextVNode(_toDisplayString(_ctx.$t('initiative.edit.header')) + " ", 1),
                    (isDirty.value)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_1, "*"))
                      : _createCommentVNode("", true)
                  ]),
                  _createVNode(_component_v_text_field, {
                    modelValue: value.value.title,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((value.value.title) = $event)),
                    onChange: update,
                    label: _ctx.$t('initiative.edit.name'),
                    hint: _ctx.$t('initiative.edit.nameHint'),
                    required: "",
                    disabled: disabled.value,
                    rules: [rules.required]
                  }, null, 8, ["modelValue", "label", "hint", "disabled", "rules"]),
                  _createVNode(_component_v_timeline, {
                    direction: "horizontal",
                    density: "compact",
                    "fill-dot": false,
                    size: "x-small",
                    align: "start"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_timeline_item, {
                        "dot-color": 
                value.value.status === _unref(InitiativeStatus).Editing
                  ? 'primary'
                  : '#efefef'
              
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_chip, {
                            variant: "flat",
                            density: "compact",
                            color: 
                  value.value.status === _unref(InitiativeStatus).Editing
                    ? 'primary'
                    : '#808080'
                
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_ctx.$t('initiative.edit.status_editing')), 1)
                            ]),
                            _: 1
                          }, 8, ["color"])
                        ]),
                        _: 1
                      }, 8, ["dot-color"]),
                      _createVNode(_component_v_timeline_item, {
                        "dot-color": 
                value.value.status === _unref(InitiativeStatus).ReadyForReview
                  ? 'primary'
                  : '#efefef'
              
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_chip, {
                            variant: "flat",
                            density: "compact",
                            color: 
                  value.value.status === _unref(InitiativeStatus).ReadyForReview
                    ? 'primary'
                    : '#808080'
                
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_ctx.$t('initiative.edit.status_review_long')), 1)
                            ]),
                            _: 1
                          }, 8, ["color"])
                        ]),
                        _: 1
                      }, 8, ["dot-color"]),
                      _createVNode(_component_v_timeline_item, {
                        "dot-color": 
                value.value.status === _unref(InitiativeStatus).ReadyForPublishing
                  ? 'primary'
                  : '#efefef'
              
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_chip, {
                            variant: "flat",
                            density: "compact",
                            color: 
                  value.value.status === _unref(InitiativeStatus).ReadyForPublishing
                    ? 'primary'
                    : '#808080'
                
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_ctx.$t('initiative.edit.status_publishing_long')), 1)
                            ]),
                            _: 1
                          }, 8, ["color"])
                        ]),
                        _: 1
                      }, 8, ["dot-color"]),
                      _createVNode(_component_v_timeline_item, {
                        "dot-color": 
                value.value.status === _unref(InitiativeStatus).Published
                  ? 'primary'
                  : '#efefef'
              
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_chip, {
                            variant: "flat",
                            density: "compact",
                            color: 
                  value.value.status === _unref(InitiativeStatus).Published
                    ? 'primary'
                    : '#808080'
                
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_ctx.$t('initiative.edit.status_published')), 1)
                            ]),
                            _: 1
                          }, 8, ["color"])
                        ]),
                        _: 1
                      }, 8, ["dot-color"])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_text_field, {
                    modelValue: value.value.quizTitle,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((value.value.quizTitle) = $event)),
                    onChange: update,
                    label: _ctx.$t('initiative.edit.quizTitle'),
                    hint: _ctx.$t('initiative.edit.quizTitleHint'),
                    required: "",
                    counter: "",
                    disabled: disabled.value,
                    rules: [rules.required],
                    class: "mt-4"
                  }, null, 8, ["modelValue", "label", "hint", "disabled", "rules"]),
                  _createVNode(_component_v_text_field, {
                    label: _ctx.$t('initiative.edit.website'),
                    hint: _ctx.$t('initiative.edit.websiteHint'),
                    modelValue: value.value.website,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((value.value.website) = $event)),
                    onChange: update,
                    disabled: disabled.value,
                    placeholder: "https://",
                    rules: [rules.required],
                    required: ""
                  }, null, 8, ["label", "hint", "modelValue", "disabled", "rules"]),
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, null, {
                        default: _withCtx(() => [
                          _createVNode(RichTextField, {
                            label: _ctx.$t('initiative.edit.shortDescription'),
                            hint: _ctx.$t('initiative.edit.shortDescriptionHint'),
                            height: 200,
                            modelValue: value.value.shortDescription,
                            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((value.value.shortDescription) = $event)),
                            onChange: update,
                            disabled: disabled.value,
                            media: value.value.media,
                            rules: [rules.required]
                          }, null, 8, ["label", "hint", "modelValue", "disabled", "media", "rules"]),
                          _createVNode(RichTextField, {
                            label: _ctx.$t('initiative.edit.description'),
                            hint: _ctx.$t('initiative.edit.descriptionHint'),
                            modelValue: value.value.description,
                            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((value.value.description) = $event)),
                            onChange: update,
                            disabled: disabled.value,
                            media: value.value.media,
                            rules: [rules.required]
                          }, null, 8, ["label", "hint", "modelValue", "disabled", "media", "rules"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_v_col, { cols: "1" }, {
                        default: _withCtx(() => [
                          _createVNode(HelpDialog, null, {
                            default: _withCtx(() => [
                              _createElementVNode("h2", null, _toDisplayString(_ctx.$t('initiative.help.descriptionHeader')), 1),
                              _createElementVNode("p", _hoisted_2, _toDisplayString(_ctx.$t('initiative.help.descriptionText1')), 1)
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_select, {
                    modelValue: value.value.impactArea,
                    "onUpdate:modelValue": [
                      _cache[5] || (_cache[5] = ($event: any) => ((value.value.impactArea) = $event)),
                      update
                    ],
                    items: _unref(translatedAreaItems),
                    label: _ctx.$t('initiative.edit.area'),
                    hint: _ctx.$t('initiative.edit.areaHint'),
                    required: "",
                    onChange: update,
                    disabled: disabled.value,
                    rules: [rules.required]
                  }, null, 8, ["modelValue", "items", "label", "hint", "disabled", "rules"]),
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, null, {
                        default: _withCtx(() => [
                          (value.value.mainCategory)
                            ? (_openBlock(), _createBlock(CategoryField, {
                                key: 0,
                                main: "",
                                modelValue: value.value.mainCategory,
                                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((value.value.mainCategory) = $event)),
                                onChange: update,
                                disabled: "",
                                label: _ctx.$t('initiative.edit.mainCategory')
                              }, null, 8, ["modelValue", "label"]))
                            : _createCommentVNode("", true),
                          _createVNode(CategoriesField, {
                            modelValue: value.value.categories,
                            "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((value.value.categories) = $event)),
                            onChange: update,
                            disabled: disabled.value
                          }, null, 8, ["modelValue", "disabled"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_v_col, { cols: "1" }, {
                        default: _withCtx(() => [
                          _createVNode(HelpDialog, null, {
                            default: _withCtx(() => [
                              _createElementVNode("h2", null, _toDisplayString(_ctx.$t('initiative.help.categoryHeader')), 1),
                              _createElementVNode("p", _hoisted_3, _toDisplayString(_ctx.$t('initiative.help.categoryText1')), 1),
                              _createElementVNode("p", _hoisted_4, _toDisplayString(_ctx.$t('initiative.help.categoryText2')), 1)
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, null, {
                        default: _withCtx(() => [
                          _createVNode(AddressField, {
                            modelValue: value.value.address,
                            "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((value.value.address) = $event)),
                            onChange: update,
                            disabled: disabled.value
                          }, null, 8, ["modelValue", "disabled"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_v_col, { cols: "1" }, {
                        default: _withCtx(() => [
                          _createVNode(HelpDialog, null, {
                            default: _withCtx(() => [
                              _createElementVNode("h2", null, _toDisplayString(_ctx.$t('initiative.help.addressHeader')), 1),
                              _createElementVNode("p", _hoisted_5, _toDisplayString(_ctx.$t('initiative.help.addressText1')), 1),
                              _createElementVNode("p", _hoisted_6, _toDisplayString(_ctx.$t('initiative.help.addressText2')), 1)
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, null, {
                        default: _withCtx(() => [
                          (value.value.contacts)
                            ? (_openBlock(), _createBlock(ContactField, {
                                key: 0,
                                modelValue: value.value.contacts,
                                "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((value.value.contacts) = $event)),
                                onChange: update,
                                onRemove: removeContact,
                                disabled: disabled.value
                              }, null, 8, ["modelValue", "disabled"]))
                            : _createCommentVNode("", true)
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_v_col, { cols: "1" }, {
                        default: _withCtx(() => [
                          _createVNode(HelpDialog, null, {
                            default: _withCtx(() => [
                              _createElementVNode("h2", null, _toDisplayString(_ctx.$t('initiative.help.contactHeader')), 1),
                              _createElementVNode("p", _hoisted_7, _toDisplayString(_ctx.$t('initiative.help.contactText1')), 1)
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, null, {
                        default: _withCtx(() => [
                          _createVNode(MediaList, {
                            modelValue: value.value,
                            "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((value).value = $event)),
                            onChange: update,
                            top: "",
                            disabled: disabled.value,
                            required: [_unref(MediaPurpose).BackgroundImage, _unref(MediaPurpose).Main]
                          }, null, 8, ["modelValue", "disabled", "required"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_v_col, { cols: "1" }, {
                        default: _withCtx(() => [
                          _createVNode(HelpDialog, null, {
                            default: _withCtx(() => [
                              _createElementVNode("h2", null, _toDisplayString(_ctx.$t('initiative.help.mediaHeader')), 1),
                              _createElementVNode("p", _hoisted_8, _toDisplayString(_ctx.$t('initiative.help.mediaText1')), 1),
                              _createElementVNode("p", _hoisted_9, _toDisplayString(_ctx.$t('initiative.help.mediaText2')), 1),
                              _createElementVNode("p", _hoisted_10, _toDisplayString(_ctx.$t('initiative.help.mediaText3')), 1)
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, { cols: "3" }, {
                default: _withCtx(() => [
                  _createVNode(InitiativeState, {
                    translation: value.value.translated,
                    media: value.value.media
                  }, null, 8, ["translation", "media"]),
                  _createVNode(_component_v_card, { class: "mt-8" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_card_text, null, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_btn, {
                            type: "submit",
                            color: "primary",
                            block: "",
                            disabled: disabled.value
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_ctx.$t('initiative.edit.save')), 1)
                            ]),
                            _: 1
                          }, 8, ["disabled"]),
                          (value.value.status === _unref(InitiativeStatus).Editing)
                            ? (_openBlock(), _createBlock(_component_v_btn, {
                                key: 0,
                                class: "mt-4",
                                onClick: setReady,
                                color: "success",
                                block: ""
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(_ctx.$t('initiative.edit.readyForReview')), 1)
                                ]),
                                _: 1
                              }))
                            : _createCommentVNode("", true),
                          (
                  value.value.status === _unref(InitiativeStatus).ReadyForReview &&
                  user.value?.level === _unref(UserLevel).ContentEditor
                )
                            ? (_openBlock(), _createBlock(_component_v_btn, {
                                key: 1,
                                class: "mt-4",
                                onClick: setReadyForPublishing,
                                color: "success",
                                block: ""
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(_ctx.$t('initiative.edit.readyForPublishing')), 1)
                                ]),
                                _: 1
                              }))
                            : _createCommentVNode("", true),
                          (
                  value.value.status === _unref(InitiativeStatus).ReadyForReview &&
                  user.value?.level === _unref(UserLevel).ContentEditor
                )
                            ? (_openBlock(), _createBlock(_component_v_btn, {
                                key: 2,
                                class: "mt-4",
                                onClick: setBackToEditing,
                                color: "info",
                                block: ""
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(_ctx.$t('initiative.edit.editing')), 1)
                                ]),
                                _: 1
                              }))
                            : _createCommentVNode("", true),
                          (user.value?.level === _unref(UserLevel).ContentEditor)
                            ? (_openBlock(), _createBlock(_component_v_btn, {
                                key: 3,
                                class: "mt-4",
                                onClick: share,
                                color: "indigo-lighten-3\n",
                                inverted: "",
                                block: ""
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(_ctx.$t('initiative.edit.share')), 1)
                                ]),
                                _: 1
                              }))
                            : _createCommentVNode("", true)
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    _: 1
  }))
}
}

})